import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import type { IslandStep } from '../../components/StepIsland/StepIsland';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import ResultEntryDetail, { type ResultEntryDetailHandle } from './ResultEntryDetail';
import {
  ResultEntryQueryPanel, EMPTY_REE_FILTERS, EMPTY_REE_OPTIONS,
  type ReeFilters, type ReeOptions,
} from './ResultEntryQueryPanel';

// ─── Types (mirror ResultEntryListItem in the API) ───────────────────────────
export interface ResultEntryJob {
  jobID:        string;
  jobOid:       string;
  projectID:    string | null;
  projectName:  string | null;
  client:       string | null;
  receivedDate: string | null;
  statusText:   string;
  statusId:     string;
  pendingCount: number;
}

type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

function cutoff(key: DateRangeKey): Date | null {
  if (key === 'All') return null;
  const n = new Date();
  const m: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(n.getFullYear(), n.getMonth() - 1, n.getDate()),
    '3M': () => new Date(n.getFullYear(), n.getMonth() - 3, n.getDate()),
    '6M': () => new Date(n.getFullYear(), n.getMonth() - 6, n.getDate()),
    '1Y': () => new Date(n.getFullYear() - 1, n.getMonth(), n.getDate()),
    '2Y': () => new Date(n.getFullYear() - 2, n.getMonth(), n.getDate()),
    '5Y': () => new Date(n.getFullYear() - 5, n.getMonth(), n.getDate()),
  };
  return m[key as Exclude<DateRangeKey, 'All'>]();
}

const ymd = (d: Date) => {
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Build a short human-readable label from the applied query panel filters.
 *  Priority: Batch ID > Job ID > Client > Project > Test > fallback count. */
function reeQueryLabel(f: ReeFilters, jobCount: number): string {
  const join = (arr: string[]) =>
    arr.slice(0, 3).join(', ') + (arr.length > 3 ? ` +${arr.length - 3} more` : '');
  if (f.batchId.length)     return `Batch: ${join(f.batchId)}`;
  if (f.id.length)          return `Job: ${join(f.id)}`;
  if (f.clientName.length)  return `Client: ${join(f.clientName)}`;
  if (f.projectId.length)   return `Project: ${join(f.projectId)}`;
  if (f.projectName.length) return `Project: ${join(f.projectName)}`;
  if (f.test.length)        return `Test: ${join(f.test)}`;
  return `${jobCount} job${jobCount === 1 ? '' : 's'}`;
}

/**
 * Result Entry Express (Analysis) — manual result entry. Single-route container:
 * the pending-entry LIST, and on Load the editable DETAIL (ResultEntryDetail),
 * with the Dynamic-Island stepper spanning both (Select Job → Load → Enter →
 * Save → Submit). Ported from legacy XAF Result Entry Express (read path live;
 * writes gated to a non-prod DB until approved).
 *
 * The structured query panel (ResultEntryQueryPanel) narrows the Job/Batch list
 * server-side before selection; the fuzzy-search row below it quick-filters the
 * returned rows. Both are distinct from the workflow Load action.
 */
export default function ResultEntryExpressPage() {
  const notify = useNotify();
  const [rows, setRows] = useState<ResultEntryJob[]>([]);
  const [loading, setLoading] = useState(false);
  // Grid stays EMPTY on open — records load only after the user runs a query.
  const [queried, setQueried] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [_error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>('3M');
  const [_selected, setSelected] = useState<Set<string | number>>(new Set());
  const [detailJob, setDetailJob] = useState<ResultEntryJob | null>(null);
  /** Multi-job mode: QC Batch matched 2+ jobs — all their samples load together. */
  const [detailJobs, setDetailJobs] = useState<ResultEntryJob[]>([]);
  const [_detailSteps, setDetailSteps] = useState<{ resultsDone: boolean; savedOnce: boolean; canSave: boolean; canSubmit: boolean }>({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
  const [detailLoadMs, setDetailLoadMs] = useState<number | null>(null);
  const [qpCollapsed, setQpCollapsed] = useState(false);
  const toggleQuery = useCallback(() => setQpCollapsed(c => !c), []);
  const detailRef = useRef<ResultEntryDetailHandle>(null);

  // Query panel — `draft` is edited in the panel; `applied` is what the query
  // actually uses (Apply copies draft → applied and reloads).
  const [draft, setDraft] = useState<ReeFilters>(EMPTY_REE_FILTERS);
  const [applied, setApplied] = useState<ReeFilters>(EMPTY_REE_FILTERS);
  // Always JOBID: the List SP's @BatchId filter narrows the job list to jobs in the
  // selected batch, which is exactly what the FilterOptions SP guarantees will return
  // results (it only offers batches whose jobs have pending regular parameters).
  // BATCHID mode required pending QC parameters on the batch itself — a different
  // condition that didn't match what the dropdown offered → "No jobs match".
  const searchBy: 'JOBID' | 'BATCHID' = 'JOBID';
  const [options, setOptions] = useState<ReeOptions>(EMPTY_REE_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // The date window for the list + options: the quick Date-Range dropdown, unless
  // the panel's explicit Date Received overrides it (panel wins when set).
  const rangeWindow = useCallback(() => {
    const co = cutoff(dateRange);
    return { start: co ? ymd(co) : undefined, end: co ? ymd(new Date()) : undefined };
  }, [dateRange]);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setSelected(new Set());
    const t0 = performance.now();
    try {
      const win = rangeWindow();
      const params: Record<string, string> = { searchBy };
      const start = applied.receivedFrom || win.start;
      const end   = applied.receivedTo   || win.end;
      if (start) params.startDate = start;
      if (end)   params.endDate   = end;
      // Checked-combo filters are multi-value → send them '|'-joined (the SP
      // matches ANY via STRING_SPLIT). Empty array = omit = no filter.
      const joinv = (a: string[]) => a.filter(Boolean).join('|');
      if (applied.clientName.length)     params.clientName     = joinv(applied.clientName);
      if (applied.projectId.length)      params.projectId      = joinv(applied.projectId);
      if (applied.projectName.length)    params.projectName    = joinv(applied.projectName);
      if (applied.test.length)           params.test           = joinv(applied.test);
      if (applied.matrix.length)         params.matrix         = joinv(applied.matrix);
      if (applied.id.length)             params.id             = joinv(applied.id);
      if (applied.batchId.length)        params.batchId        = joinv(applied.batchId);
      if (applied.sampleLocation.length) params.sampleLocation = joinv(applied.sampleLocation);
      if (applied.analyzedFrom)          params.analyzedFrom   = applied.analyzedFrom;
      if (applied.analyzedTo)            params.analyzedTo     = applied.analyzedTo;
      if (applied.collectedFrom)         params.collectedFrom  = applied.collectedFrom;
      if (applied.collectedTo)           params.collectedTo    = applied.collectedTo;

      const res = await api.get('/api/v1/result-entry', { params });
      setRows((res.data as { data: ResultEntryJob[] }).data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [rangeWindow, searchBy, applied]);

  // No auto-load on open — the results grid is empty until the user applies a
  // query. After the first Apply Query, changes to the criteria re-run the load.
  useEffect(() => { if (queried) void load(); }, [load, queried]);

  // Dropdown options for the current mode + base date window (independent of the
  // in-progress filters, so the pickers stay stable while the analyst narrows).
  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const win = rangeWindow();
      const params: Record<string, string> = { searchBy: 'JOBID' };   // options always job-centric
      if (win.start) params.startDate = win.start;
      if (win.end)   params.endDate   = win.end;
      const res = await api.get('/api/v1/result-entry/filter-options', { params, silentLoading: true } as never);
      const list = (res.data as { data: { kind: string; value: string; col2?: string; col3?: string }[] }).data ?? [];
      const by = (k: string) => list.filter(o => o.kind === k).map(o => o.value);
      // Sort Job ID and QC Batch ID Z→A (latest IDs first, like an email inbox).
      const jobIdsSorted = by('JobId').sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
      const batchIdsSorted = list
        .filter(o => o.kind === 'BatchId')
        .map(o => ({ id: o.value, test: o.col2 ?? '', emp: o.col3 ?? '' }))
        .sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true, sensitivity: 'base' }));
      setOptions({
        clients: by('Client'), projects: by('Project'), projectNames: by('ProjectName'),
        tests: by('Test'), matrices: by('Matrix'), jobIds: jobIdsSorted,
        sampleLocations: by('SampleLocation'),
        // QC Batch carries Test (col2) + creating Employee (col3) for its 3-column picker.
        batchIds: batchIdsSorted,
      });
    } catch {
      setOptions(EMPTY_REE_OPTIONS);   // pickers just show no options; not fatal
    } finally {
      setOptionsLoading(false);
    }
  }, [rangeWindow, searchBy]);

  useEffect(() => { void loadOptions(); }, [loadOptions]);

  // Open / close the inline result grid — must be declared before loadAction so
  // the auto-open paths can call them directly.
  const openDetail = useCallback((job: ResultEntryJob) => {
    setDetailJob(job);
    setDetailJobs([]);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(true);   // auto-collapse query panel when detail opens
    window.dispatchEvent(new CustomEvent('ai:nav-collapse'));
  }, []);

  /** Multi-job mode: all jobs in a QC batch load their samples together. */
  const openMultiDetail = useCallback((jobList: ResultEntryJob[]) => {
    setDetailJobs(jobList);
    setDetailJob(null);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(true);
    window.dispatchEvent(new CustomEvent('ai:nav-collapse'));
  }, []);

  const closeDetail = useCallback(() => {
    setDetailJob(null);
    setDetailJobs([]);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(false);  // restore query panel when returning to list
    void load();
  }, [load]);

  // Set true right before a query so that, once it returns, jobs are auto-opened —
  // single job → openDetail, multiple jobs → openMultiDetail (no picker shown).
  const pendingAutoLoad = useRef(false);

  // The merged “Load” action: run the query and auto-open when a single job is
  // returned. Multiple matches → notify the user to narrow the filter.
  const loadAction = useCallback(() => {
    if (draft.receivedFrom && draft.receivedTo && draft.receivedFrom > draft.receivedTo) {
      notify.warning('Date Received “From” must be on or before “To”.');
      return;
    }
    if (draft.analyzedFrom && draft.analyzedTo && draft.analyzedFrom > draft.analyzedTo) {
      notify.warning('Date Analyzed “From” must be on or before “To”.');
      return;
    }
    if (draft.collectedFrom && draft.collectedTo && draft.collectedFrom > draft.collectedTo) {
      notify.warning('Date Collected “From” must be on or before “To”.');
      return;
    }
    pendingAutoLoad.current = true;
    setQueried(true);
    setApplied(draft);   // triggers load() via the effect
  }, [draft, notify]);

  // Auto-open when the query returns results — 1 job → single-job detail;
  // 2+ jobs → multi-job detail (all samples combined, no picker shown ever).
  useEffect(() => {
    if (pendingAutoLoad.current && !loading) {
      pendingAutoLoad.current = false;
      if (rows.length === 1 && rows[0]) {
        openDetail(rows[0]);
      } else if (rows.length > 1) {
        openMultiDetail(rows);
      }
    }
  }, [loading, rows, openDetail, openMultiDetail]);

  const clearQuery = useCallback(() => {
    setDraft(EMPTY_REE_FILTERS);
    setApplied(EMPTY_REE_FILTERS);
    // Return to the initial state: no job loaded, Query Panel expanded.
    pendingAutoLoad.current = false;
    setQueried(false);
    setRows([]); setSelected(new Set()); setLoadMs(null); setError(null);
    // Clear the result-entry grid — drop the loaded job so it returns to its
    // empty-shell state (same as page open, before Load is clicked).
    setDetailJob(null);
    setDetailJobs([]);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(false);   // expand query panel so the user can refine criteria
  }, []);

  // Refresh: always re-pull the option lists; re-run the grid only if a query has
  // been applied (before that the grid is intentionally empty).
  const refresh = useCallback(() => {
    void loadOptions();
    if (queried) void load();
  }, [loadOptions, load, queried]);

  const inDetail = !!detailJob || detailJobs.length > 0;

  // ── Stepper — Dynamic-Island bar spanning all 5 workflow steps. ─────────────
  const stepperSteps: IslandStep[] = [
    { id: 'select', label: 'Select Job/Batch', state: inDetail ? 'done' : 'active' },
    { id: 'load',   label: 'Load Samples',     state: inDetail ? 'done' : (queried ? 'active' : 'todo') },
    { id: 'enter',  label: 'Enter Results',    state: !inDetail ? 'todo' : _detailSteps.resultsDone ? 'done' : 'active' },
    { id: 'save',   label: 'Save',             state: !inDetail ? 'todo' : _detailSteps.savedOnce ? 'done' : (_detailSteps.resultsDone ? 'active' : 'todo') },
    { id: 'submit', label: 'Submit',           state: !inDetail ? 'todo' : _detailSteps.savedOnce ? 'active' : 'todo' },
  ];

  // ── Single merged page ─────────────────────────────────────────────────────
  return (
    <div className="bl-page">
      <TopbarStepper steps={stepperSteps} />
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Analysis</span><i className="ti ti-chevron-right" />
            <span className={(detailJob || detailJobs.length > 0) ? 'dp-bc-link' : 'dp-bc-active'}
              style={(detailJob || detailJobs.length > 0) ? { cursor: 'pointer' } : undefined}
              onClick={(detailJob || detailJobs.length > 0) ? closeDetail : undefined}>
              Result Entry Express
            </span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="resexpress"
              title={detailJob
                ? `Result Entry Express — ${
                    (applied.batchId.length > 0 || applied.id.length > 0
                     || applied.clientName.length > 0 || applied.test.length > 0
                     || applied.projectName.length > 0)
                      ? reeQueryLabel(applied, 1)
                      : detailJob.jobID}`
                : detailJobs.length > 0
                  ? `Result Entry Express — ${reeQueryLabel(applied, detailJobs.length)}`
                  : undefined}
              auditFormName="Result Entry"
              auditSourceId={detailJob?.jobOid}
              loadMs={(detailJob || detailJobs.length > 0) ? detailLoadMs : loadMs}
              loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Compact control bar: [Date Range | Refresh | Query toggle]   [Clear | Load] */}
        <div className="ree-ctrlbar">
          <div className="ree-ctrlbar-left">
            <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
              value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
              onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
            <button type="button" className="ca-btn" onClick={refresh} disabled={loading}
              title="Reload the current results.">
              <i className={`ti ti-refresh${loading ? ' ree-spin' : ''}`} aria-hidden="true" />&nbsp;Refresh
            </button>
            <button type="button"
              className={`ca-btn ree-query-btn${qpCollapsed ? '' : ' ree-query-btn-active'}`}
              onClick={toggleQuery}
              title={qpCollapsed ? 'Show query filters' : 'Hide query filters'}>
              <i className="ti ti-filter" aria-hidden="true" />&nbsp;Query&nbsp;
              <i className={`ti ti-chevron-${qpCollapsed ? 'down' : 'up'}`} aria-hidden="true" />
            </button>
          </div>
          <div className="ree-ctrlbar-right">
            <button type="button" className="ca-btn" onClick={clearQuery} disabled={loading}
              title="Reset all query criteria and clear the grid.">
              <i className="ti ti-eraser" aria-hidden="true" />&nbsp;Clear
            </button>
            <button type="button" className="ca-btn ca-btn-solid" onClick={loadAction} disabled={loading || inDetail}
              title={inDetail ? 'Clear the current job first, then run a new query.' : 'Run the query and load the matching job for result entry.'}>
              {loading
                ? <><i className="ti ti-loader-2 ree-spin" aria-hidden="true" />&nbsp;Loading…</>
                : 'Load'}
            </button>
          </div>
        </div>

        {/* Structured query panel — always visible (Query Panel stays on every
            state: browsing, selecting, or viewing results). */}
        <ResultEntryQueryPanel
          filters={draft}
          onChange={(patch) => setDraft(prev => ({ ...prev, ...patch }))}
          options={options}
          loadingOptions={optionsLoading}
          applying={loading}
          onApply={loadAction}
          onClear={clearQuery}
          collapsed={qpCollapsed}
          onCollapsedChange={setQpCollapsed}
          pendingBatches={options.batchIds.length}
          pendingJobs={options.jobIds.length}
          pendingLoading={optionsLoading}
        />

        {/* No-results notice */}
        {queried && !inDetail && rows.length === 0 && !loading && (
          <div className="ree-no-results">
            <i className="ti ti-inbox" aria-hidden="true" />
            No jobs match the current filters.
          </div>
        )}

        {/* Result entry grid — always visible. Empty state until Load opens a job. */}
        <ResultEntryDetail
          ref={detailRef}
          job={detailJob}
          jobs={detailJobs.length > 0 ? detailJobs : undefined}
          batchIds={applied.batchId.length > 0 ? applied.batchId : undefined}
          rowFilters={applied}
          kind="job"
          onBack={closeDetail}
          inline
          onStepChange={setDetailSteps}
          onLoadMs={setDetailLoadMs}
        />
      </div>
    </div>
  );
}
